import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import PersonalCards from './PersonalCards';
import BusinessCards from './BusinessCards';
import { useIsSubscribed } from '../../hooks/useIsSubscribed';
import { useTier } from '../../hooks/useTier';
import './CreditCardsInventory.css';

// Premium Access Message Component
const PremiumAccessMessage = ({ tierId, onUpgrade, t }) => {
  return (
    <div className="premium-access-container">
      <div className="premium-access-card">
        <div className="premium-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#d4a574" stroke="#c08f5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h2 className="premium-title">{t('creditCardsInventory.premiumFeatureTitle')}</h2>
        <div className="current-tier-badge">
          <span className="tier-label">{t('creditCardsInventory.currentTier')}:</span>
          <span className="tier-name">{t(`tiers.${tierId}.name`)}</span>
        </div>
        <p className="premium-description">
          {t('creditCardsInventory.premiumDescription')}
        </p>
        <div className="premium-benefits">
          <div className="benefit-item">
            <svg className="benefit-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="#27ae60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t('creditCardsInventory.benefit1')}</span>
          </div>
          <div className="benefit-item">
            <svg className="benefit-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="#27ae60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t('creditCardsInventory.benefit2')}</span>
          </div>
          <div className="benefit-item">
            <svg className="benefit-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="#27ae60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t('creditCardsInventory.benefit3')}</span>
          </div>
        </div>
        <button className="upgrade-button" onClick={onUpgrade}>
          <svg className="button-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white"/>
          </svg>
          {t('creditCardsInventory.upgradeButton')}
        </button>
      </div>
    </div>
  );
};

const CreditCardsInventory = () => {
  const t = useTranslation();
  const [activeTab, setActiveTab] = useState('personal');
  const { isSubscribed, loading: subscriptionLoading } = useIsSubscribed();
  const { hasInventoryAccess, tierId, loading: tierLoading } = useTier();
  const history = useHistory();

  return (
    <div className="credit-cards-inventory">
      <div className="inventory-header">
        <h1>{t('creditCardsInventory.title')}</h1>
        <p>{t('creditCardsInventory.subtitle')}</p>
      </div>

      <div className="inventory-tabs">
        <button
          className={`tab-button ${activeTab === 'personal' ? 'active' : ''} ${!hasInventoryAccess('personal_credit_cards') && !subscriptionLoading && !tierLoading ? 'disabled-tab' : ''}`}
          onClick={() => {
            if (!subscriptionLoading && !tierLoading && !hasInventoryAccess('personal_credit_cards')) {
              // Redirect to subscription page if tier doesn't have access
              history.push('/manage-subscription');
            } else {
              setActiveTab('personal');
            }
          }}
          title={!hasInventoryAccess('personal_credit_cards') && !subscriptionLoading && !tierLoading ? t('creditCardsInventory.premiumFeature') : ''}
        >
          {t('creditCardsInventory.personalCards')}
          {!hasInventoryAccess('personal_credit_cards') && !subscriptionLoading && !tierLoading && (
            <span style={{ marginLeft: '8px', fontSize: '14px' }}>🔒</span>
          )}
        </button>
        <button
          className={`tab-button ${activeTab === 'business' ? 'active' : ''} ${!hasInventoryAccess('business_credit_cards') && !subscriptionLoading && !tierLoading ? 'disabled-tab' : ''}`}
          onClick={() => {
            if (!subscriptionLoading && !tierLoading && !hasInventoryAccess('business_credit_cards')) {
              // Redirect to subscription page if tier doesn't have access
              history.push('/manage-subscription');
            } else {
              setActiveTab('business');
            }
          }}
          title={!hasInventoryAccess('business_credit_cards') && !subscriptionLoading && !tierLoading ? t('creditCardsInventory.premiumFeature') : ''}
        >
          {t('creditCardsInventory.businessCards')}
          {!hasInventoryAccess('business_credit_cards') && !subscriptionLoading && !tierLoading && (
            <span style={{ marginLeft: '8px', fontSize: '14px' }}>🔒</span>
          )}
        </button>
      </div>

      <div className="inventory-content">
        {subscriptionLoading || tierLoading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div className="spinner"></div>
            <p>{t('creditCardsInventory.checkingSubscription')}</p>
          </div>
        ) : activeTab === 'personal' ? (
          hasInventoryAccess('personal_credit_cards') ? (
            <PersonalCards />
          ) : (
            <PremiumAccessMessage
              tierId={tierId}
              onUpgrade={() => history.push('/manage-subscription')}
              t={t}
            />
          )
        ) : (
          hasInventoryAccess('business_credit_cards') ? (
            <BusinessCards />
          ) : (
            <PremiumAccessMessage
              tierId={tierId}
              onUpgrade={() => history.push('/manage-subscription')}
              t={t}
            />
          )
        )}
      </div>
    </div>
  );
};

export default CreditCardsInventory;

